import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, FileText, CreditCard, Zap, Shield, TrendingUp, AlertCircle, Clock } from 'lucide-react';
import AnalysisForm from './AnalysisForm';

const AnalysisSection = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);

  const handleFormSuccess = (result) => {
    setSubmissionResult(result);
    setIsFormOpen(false);
  };

  const analysisFeatures = [
    { icon: TrendingUp, title: 'Performance Audit', description: 'Page speed, loading times, and optimization opportunities' },
    { icon: Shield, title: 'Security Review', description: 'Vulnerability assessment and security best practices' },
    { icon: Zap, title: 'SEO Analysis', description: 'Search engine optimization and ranking factors' },
    { icon: FileText, title: 'Code Quality', description: 'Code structure, maintainability, and technical debt' },
    { icon: AlertCircle, title: 'User Experience', description: 'UI/UX review and accessibility compliance' },
    { icon: Clock, title: 'Scalability Assessment', description: 'Architecture review and growth potential' }
  ];

  const reportIncludes = [
    'Detailed performance metrics with benchmarks',
    'Security vulnerability report with fixes',
    'SEO optimization checklist',
    'Code quality assessment',
    'User experience recommendations',
    'Scalability roadmap',
    'Priority action items with timeline',
    'Cost-benefit analysis for improvements',
    'Personal 5-minute video walkthrough (Loom) explaining the findings'
  ];

  const openAnalysisForm = () => {
    setIsFormOpen(true);
  };

  return (
    <section id="analysis" className="py-20 relative">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center bg-red-600/10 border border-red-500/30 rounded-full px-4 py-2 mb-6">
            <CheckCircle className="w-4 h-4 text-red-400 mr-2" />
            <span className="text-red-300 text-sm">Professional Website Analysis</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Get Your Website
            <span className="block pb-2 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">
              Analyzed by Experts
            </span>
          </h2>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            Comprehensive website analysis for just KES 3,450. Get detailed insights on performance, 
            security, SEO, and scalability with actionable recommendations.
          </p>

          {/* Price Badge */}
          <motion.div
            className="inline-flex items-center bg-gradient-to-br from-red-500 to-orange-700 rounded-full px-6 py-3 mb-8 shadow-lg ring-1 ring-inset ring-white/25"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <span className="text-white font-bold text-2xl">KES 3,450</span>
            <span className="text-white/80 ml-2">per analysis</span>
          </motion.div>
          <p className="text-sm text-gray-400 text-center">
            100% of this fee is credited back to you if you book a redesign or any technical service with us
          </p>
        </motion.div>

        {/* Analysis Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {analysisFeatures.map((feature, index) => (
            <motion.div
              key={feature.title}
              className="analysis-card bg-white/8 backdrop-blur-md border border-white/15 rounded-xl p-6 hover:bg-white/12 transition-all duration-300 shadow-md"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <div className="w-14 h-14 bg-gradient-to-br from-red-600 to-orange-600 rounded-xl flex items-center justify-center mb-4 shadow-lg border border-red-400/30">
                <feature.icon className="w-7 h-7 text-white drop-shadow-sm" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
              <p className="text-gray-400 text-sm">{feature.description}</p>
            </motion.div>
          ))}
        </div>

        {/* What's Included */}
        <motion.div
          className="bg-gradient-to-r from-red-600/10 to-orange-600/10 border border-red-500/30 rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                <span className="w-10 h-10 bg-gradient-to-r from-red-600 to-orange-600 rounded-lg flex items-center justify-center mr-3 shadow-md flex-shrink-0">
                  <FileText className="w-5 h-5 text-white" />
                </span>
                What Your Report Includes
              </h3>
              <ul className="space-y-3">
                {reportIncludes.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-orange-400 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                <span className="w-10 h-10 bg-gradient-to-r from-orange-600 to-amber-600 rounded-lg flex items-center justify-center mr-3 shadow-md flex-shrink-0">
                  <Clock className="w-5 h-5 text-white" />
                </span>
                Process & Timeline
              </h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center text-white text-sm font-bold mr-3 mt-0.5">1</div>
                  <div>
                    <h4 className="text-white font-semibold">Submit Your Website</h4>
                    <p className="text-gray-400 text-sm">Fill out the analysis request form</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center text-white text-sm font-bold mr-3 mt-0.5">2</div>
                  <div>
                    <h4 className="text-white font-semibold">Make Payment (KES 3,450)</h4>
                    <p className="text-gray-400 text-sm">Secure payment via M-Pesa or Card</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center text-white text-sm font-bold mr-3 mt-0.5">3</div>
                  <div>
                    <h4 className="text-white font-semibold">Expert Consultation & Roadmap</h4>
                    <p className="text-gray-400 text-sm">Receive a comprehensive PDF and a recorded video analysis with a step-by-step plan to fix your website's issues</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <h3 className="text-3xl font-bold text-white mb-4">
            Ready to Optimize Your Website?
          </h3>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
            Get a clear picture of what's holding your site back — and exactly what to fix first.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <motion.button
              onClick={openAnalysisForm}
              className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center glow-orange"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FileText className="w-5 h-5 mr-2" />
              Start Website Analysis
            </motion.button>
            
            <motion.button
              className="bg-white/10 backdrop-blur-sm border border-white/20 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/20 transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            >
              <CreditCard className="w-5 h-5 mr-2 inline" />
              Payment Options
            </motion.button>
          </div>

          <p className="text-gray-400 text-sm mt-6">
            Secure payment • 24-48 hour delivery • Satisfaction guaranteed
          </p>
        </motion.div>
      </div>

      {/* Analysis Form Modal */}
      <AnimatePresence>
        {isFormOpen && (
          <AnalysisForm
            onClose={() => setIsFormOpen(false)}
            onSuccess={handleFormSuccess}
          />
        )}
      </AnimatePresence>

      {/* Success Modal */}
      <AnimatePresence>
        {submissionResult && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSubmissionResult(null)}
          >
            <motion.div
              className="bg-slate-900 border border-white/10 rounded-2xl p-8 max-w-md w-full"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">
                  Payment Received!
                </h3>
                <p className="text-gray-300 mb-6">
                  Thank you - your M-Pesa payment for the website analysis was successful. We'll email your full report within 24-48 hours.
                </p>
                <div className="bg-red-600/20 border border-red-500/30 rounded-lg p-4 mb-6">
                  <p className="text-red-400 text-sm font-medium">Request ID: {submissionResult.projectId}</p>
                  <p className="text-gray-400 text-xs mt-1">Keep this ID for your records</p>
                </div>
                <motion.button
                  className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSubmissionResult(null)}
                >
                  Got it, thanks!
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default AnalysisSection;
